import React, { ComponentProps } from 'react';
import { Col, Form, Input, Row, Select } from 'antd';
import { useQuery } from '@common/hooks';
import {
  TimeZonesQuery,
  TimeZonesQueryResponse,
  US_STATES_QUERY,
} from '@institutions/graphql';
import { ErrorHandler } from '@common/components';

type OrganizationFacilityFormProps = ComponentProps<typeof Form>;

export type FacilityFormValues = {
  name: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  stateCode: string;
  timeZone: string;
};

export const buildOrganizationBusiness = (data: FacilityFormValues) => {
  return {
    name: data['name'].trim(),
    address: data['address'].trim(),
    postalCode: data['postalCode'].trim(),
    city: data['city'].trim(),
    country: data['country'].trim(),
    stateCode: data['stateCode'],
    timeZone: data['timeZone'],
  };
};

export const OrganizationFacilityForm = (
  props: OrganizationFacilityFormProps
) => {
  const { data: timeZonesQueryData, error: timeZonesQueryError } =
    useQuery<TimeZonesQueryResponse>(TimeZonesQuery);

  const { data: usStatesQueryData, error: usStatesQueryError } =
    useQuery(US_STATES_QUERY);

  const error = timeZonesQueryError || usStatesQueryError;

  if (error) {
    return <ErrorHandler error={error} log="OrganizationFacilityForm" />;
  }

  const defaultRule = { required: true, message: "Value can't be blank" };

  return (
    <Form
      {...props}
      className="organization-facility-form__form"
      layout="vertical"
    >
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="name" label="Facility Name" rules={[defaultRule]}>
            <Input size="large" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={18}>
          <Form.Item name="address" label="Address" rules={[defaultRule]}>
            <Input size="large" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="postalCode" label="ZIP Code" rules={[defaultRule]}>
            <Input size="large" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={18}>
          <Form.Item name="city" label="City" rules={[defaultRule]}>
            <Input size="large" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="stateCode" label="State" rules={[defaultRule]}>
            <Select size="large" showSearch>
              {usStatesQueryData?.usStates.map(
                (state: { text: string; value: string }) => (
                  <Select.Option key={state.value} value={state.value}>
                    {state.text}
                  </Select.Option>
                )
              )}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={18}>
          <Form.Item name="timeZone" label="Timezone" rules={[defaultRule]}>
            <Select size="large">
              {timeZonesQueryData?.timeZones.map(
                (timeZone: { text: string; value: string }) => (
                  <Select.Option key={timeZone.value} value={timeZone.value}>
                    {timeZone.text}
                  </Select.Option>
                )
              )}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="country" label="Country">
            <Input size="large" disabled />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
